import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  DatePicker,
  Select,
  Input,
  Tag,
  Popconfirm,
  Space,
} from 'antd';
import { PlusOutlined, DeleteOutlined, FilterOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import {
  create_employee_availability,
  get_employee_availability,
  delete_employee_availability,
} from '../../../utils/shift_availability_api';
import { get_employee } from '../../../utils/api';
import { useTranslation } from 'react-i18next';
import ReusableSearchInput from '@/components/ReusableSearch';
import useTableConfig from '@/hooks/useTableConfig';
import TableColumnConfig from '@/components/TableColumnConfig';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import Icons from '@/components/third-party/Icons';
import { Typography } from 'antd';

const { RangePicker } = DatePicker;
const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

function EmployeeAvailability() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const [loading, setLoading] = useState(false);
  const [availabilityData, setAvailabilityData] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [filterType, setFilterType] = useState(null);
  const [form] = Form.useForm();

  // Availability type colors
  const typeColors = {
    AVAILABLE: '#52c41a',
    LEAVE: '#ff4d4f',
    SICK: '#fa8c16',
    TRAINING: '#1890ff',
    VACATION: '#722ed1',
    UNAVAILABLE: '#8c8c8c',
  };

  const loadAvailabilityData = async () => {
    setLoading(true);
    try {
      await get_employee_availability({}, (res) => {
        if (res.data) {
          setAvailabilityData(res.data);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error loading availability:', error);
      toast.error('Failed to load availability data');
      setLoading(false);
    }
  };

  const loadEmployeeList = async () => {
    try {
      await get_employee((res) => {
        if (res?.data?.data) {
          setEmployeeList(res.data.data);
        }
      });
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  useEffect(() => {
    loadAvailabilityData();
    loadEmployeeList();
  }, []);

  const handleSubmit = async (values) => {
    try {
      const payload = {
        employee_id: values.employee_id,
        start_date: values.dateRange[0].format('YYYY-MM-DD'),
        end_date: values.dateRange[1].format('YYYY-MM-DD'),
        availability_type: values.availability_type,
        reason: values.reason || '',
        is_full_day: true,
      };

      await create_employee_availability(payload, (res) => {
        toast.success(res.message || 'Availability record created successfully');
        setIsModalOpen(false);
        form.resetFields();
        loadAvailabilityData();
      });
    } catch (error) {
      console.error('Error creating availability:', error);
      toast.error('Failed to create availability record');
    }
  };

  const handleDelete = async (id) => {
    try {
      await delete_employee_availability(id, (res) => {
        toast.success(res.message || 'Availability record deleted successfully');
        loadAvailabilityData();
      });
    } catch (error) {
      console.error('Error deleting availability:', error);
      toast.error('Failed to delete availability record');
    }
  };

  const allColumns = [
    {
      title: 'Employee',
      dataIndex: 'employee_name',
      key: 'employee_name',
      width: '20%',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
    },
    {
      title: 'Type',
      dataIndex: 'availability_type',
      key: 'availability_type',
      width: '15%',
      align: 'center',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      render: (type) => <Tag color={typeColors[type] || 'default'}>{type}</Tag>,
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      width: '15%',
      align: 'center',
      defaultVisible: true,
      sortable: true,
      sortType: 'date',
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      width: '15%',
      align: 'center',
      defaultVisible: true,
      sortable: true,
      sortType: 'date',
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      width: '25%',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      render: (text) => text || '-',
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: '10%',
      align: 'center',
      defaultVisible: true,
      sortable: false,
      render: (_, record) => (
        <Popconfirm
          title="Delete Availability"
          description="Are you sure you want to delete this availability record?"
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <Button danger size="small" icon={<DeleteOutlined style={{ fontSize: '20px' }} />}>
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const { visibleColumns, columnConfigMenu, filteredData } = useTableConfig({
    tableKey: 'employee_availability_config',
    columns: allColumns,
    data: availabilityData,
    searchValue: searchValue,
  });

  return (
    <Card
      className="m-2.5 border border-gray-300 shadow-md"
      title={
        <div className="flex items-center gap-2">
          <Icons.Calendar className="text-blue-600" size={24} />
          <Title level={4} style={{ color: textColor, margin: 0, padding: 0 }}>
            {t('Employee Availability Management')}
          </Title>
        </div>
      }
      extra={
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsModalOpen(true)}
            style={{
              backgroundColor: isDark ? '#0F172A' : defaultColors.primary,
              borderColor: isDark ? '#0F172A' : defaultColors.primary,
            }}
          >
            Add Availability
          </Button>
          <TableColumnConfig menu={columnConfigMenu} className="mt-1" />
          <ReusableSearchInput
            placeholder="Search employee..."
            width={250}
            onSearch={(value) => setSearchValue(value)}
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </Space>
      }
    >
      <Table
        dataSource={filteredData}
        columns={visibleColumns}
        loading={loading}
        rowKey="id"
        bordered
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
        scroll={{ x: 'max-content' }}
        size="middle"
      />

      <Modal
        title="Add Employee Availability"
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item
            name="employee_id"
            label="Employee"
            rules={[{ required: true, message: 'Please select an employee' }]}
          >
            <Select
              placeholder="Select employee"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {employeeList.map((emp) => (
                <Option key={emp.employee_id} value={emp.employee_id}>
                  {emp.employee_name || emp.username}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="dateRange"
            label="Date Range"
            rules={[{ required: true, message: 'Please select date range' }]}
          >
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="availability_type"
            label="Availability Type"
            rules={[{ required: true, message: 'Please select type' }]}
          >
            <Select placeholder="Select type">
              <Option key="AVAILABLE" value="AVAILABLE">
                Available
              </Option>
              <Option key="LEAVE" value="LEAVE">
                On Leave
              </Option>
              <Option key="SICK" value="SICK">
                Sick Leave
              </Option>
              <Option key="TRAINING" value="TRAINING">
                Training
              </Option>
              <Option key="VACATION" value="VACATION">
                Vacation
              </Option>
              <Option key="UNAVAILABLE" value="UNAVAILABLE">
                Unavailable
              </Option>
            </Select>
          </Form.Item>

          <Form.Item name="reason" label="Reason">
            <TextArea rows={3} placeholder="Enter reason for unavailability..." />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setIsModalOpen(false);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Create Availability Record
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

export default EmployeeAvailability;
